import { useState, useEffect } from "react";
import { X, Settings, Bell, Mail, MessageSquare, Save } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext.tsx";
import { supabase } from "../lib/supabaseClient.ts";
import { subscribeUserToPush, unsubscribeUserFromPush, checkPushSubscription } from "../utils/notifications.ts";
import { NotificationService } from "../utils/notificationService.ts";

interface UserSettings {
  pushNotifications: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  phoneNumber: string;
}

interface UserSettingsModalProps {
  open: boolean;
  onClose: () => void;
  user: any;
}

export default function UserSettingsModal({ open, onClose, user }: UserSettingsModalProps) {
  const { t } = useLanguage();
  const [settings, setSettings] = useState<UserSettings>({
    pushNotifications: false,
    emailNotifications: true,
    smsNotifications: false,
    phoneNumber: ""
  });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (user && open) {
      loadUserSettings();
    }
  }, [user, open]);

  const loadUserSettings = async () => {
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/user-profile/${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        setSettings({
          pushNotifications: data.notification_preferences?.pushNotifications ?? false,
          emailNotifications: data.notification_preferences?.emailNotifications ?? true,
          smsNotifications: data.notification_preferences?.smsNotifications ?? false,
          phoneNumber: data.phone ?? ""
        });
        return;
      }
      
      // Fallback to localStorage
      const saved = localStorage.getItem(`agrosense_settings_${user.id}`);
      if (saved) {
        const data = JSON.parse(saved);
        setSettings({
          pushNotifications: data.pushNotifications ?? false,
          emailNotifications: data.emailNotifications ?? true,
          smsNotifications: data.smsNotifications ?? false,
          phoneNumber: data.phoneNumber ?? ""
        });
        return;
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
    
    // Use defaults if no saved settings
    setSettings({
      pushNotifications: false,
      emailNotifications: true,
      smsNotifications: false,
      phoneNumber: ""
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      
      // Save to database via API
      const response = await fetch(`${backendUrl}/api/user-profile/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: settings.phoneNumber.startsWith('+266') ? settings.phoneNumber : `+266${settings.phoneNumber.replace(/^\+?266?/, '')}`,
          display_name: user.user_metadata?.display_name || user.user_metadata?.full_name || '',
          notification_preferences: {
            pushNotifications: settings.pushNotifications,
            emailNotifications: settings.emailNotifications,
            smsNotifications: settings.smsNotifications
          }
        })
      });

      if (response.ok) {
        // Also save to localStorage as backup
        localStorage.setItem(`agrosense_settings_${user.id}`, JSON.stringify({
          pushNotifications: settings.pushNotifications,
          emailNotifications: settings.emailNotifications,
          smsNotifications: settings.smsNotifications,
          phoneNumber: settings.phoneNumber
        }));

        // Handle push notification subscription/unsubscription
        const currentPushStatus = await checkPushSubscription();
        if (settings.pushNotifications && !currentPushStatus) {
          await subscribeUserToPush();
        } else if (!settings.pushNotifications && currentPushStatus) {
          await unsubscribeUserFromPush(user);
        }

        setMessage(t("settings.settingsSaved"));
        setTimeout(() => {
          setMessage("");
          onClose();
        }, 1500);
      } else {
        throw new Error('Failed to save to database');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage(t("settings.settingsFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotifications = async () => {
    setTesting(true);
    setMessage("");
    
    try {
      // Send a real farming notification instead of test
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/notifications/test-weather`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          phone: settings.phoneNumber,
          email: user.email,
          name: user.user_metadata?.display_name || 'Farmer'
        })
      });

      if (response.ok) {
        setMessage("Weather notification sent! Check your phone and email.");
      } else {
        setMessage("Failed to send notification. Please check your settings.");
      }
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage("Failed to send notification");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setTesting(false);
    }
  };

  const validatePhoneNumber = (phone: string) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-md p-0 mx-auto bg-white shadow-xl rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t("settings.userSettings")}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Notifications Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t("settings.notifications")}</h3>
            <div className="space-y-3">
              {/* Push Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Bell className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{t("settings.pushNotifications")}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, pushNotifications: !prev.pushNotifications }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.pushNotifications ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.pushNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Email Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{t("settings.emailNotifications")}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, emailNotifications: !prev.emailNotifications }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.emailNotifications ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* SMS Notifications */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{t("settings.smsNotifications")}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings(prev => ({ ...prev, smsNotifications: !prev.smsNotifications }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.smsNotifications ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.smsNotifications ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Contact Info Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t("settings.contactInformation")}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("settings.phoneNumberLesotho")}
                </label>
                {!editing && settings.phoneNumber ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-md">
                    <span className="text-gray-900">{settings.phoneNumber.startsWith('+266') ? settings.phoneNumber : `+266${settings.phoneNumber}`}</span>
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {t("settings.edit")}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="inline-flex items-center px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-md">
                        +266
                      </span>
                      <input
                        type="tel"
                        value={settings.phoneNumber.replace(/^\+?266/, '')}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '');
                          if (digits.length <= 8) {
                            setSettings(prev => ({ ...prev, phoneNumber: `+266${digits}` }));
                          }
                        }}
                        placeholder="57586176"
                        maxLength={8}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    {editing && (
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(false);
                            loadUserSettings();
                          }}
                          className="px-3 py-1 text-sm text-gray-600 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200"
                        >
                          {t("settings.cancel")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            handleSave();
                            setEditing(false);
                          }}
                          disabled={settings.phoneNumber.replace(/^\+?266/, '').length !== 8}
                          className="px-3 py-1 text-sm text-white bg-green-600 border border-transparent rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {t("settings.save")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {settings.phoneNumber.replace(/^\+?266/, '').length > 0 && settings.phoneNumber.replace(/^\+?266/, '').length !== 8 && (
                  <p className="mt-1 text-xs text-red-600">{t("settings.enterExactly8Digits")}</p>
                )}
              </div>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`p-3 rounded-md text-sm ${
              message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {message}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          {!editing && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t("settings.saving")}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t("settings.saveSettings")}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}